/**
 * Catálogo público GUEST-FIRST: explorar cursos sin cuenta (el catálogo es
 * autoritativo del servidor, REST `/api/catalog`). Inscribirse guarda progreso
 * local; CREAR requiere cuenta. Tarjetas vibrantes con color por curso.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { msg } from "@lingui/core/macro";
import { BookOpen, Gift, Search, Sparkles, Users } from "lucide-solid";
import { type CatalogEntryDTO, cn, courseQueryKeys, fetchCatalog, isWorkshop } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@/lib/u-router";
import { SERVER_URL } from "@/lib/runtime-config";
import { Skeleton } from "@/components/ui/skeleton";
import { Mascot } from "@/components/ui/mascot";
import { listEntranceProps, useListEntrance } from "@/lib/animation/list-entrance";

/** Color joy determinista por curso (variedad alegre pero armónica). */
const JOY_TONES = [
  { chip: "bg-joy-grape text-joy-grape-foreground", soft: "bg-joy-grape/12" },
  { chip: "bg-joy-sky text-joy-sky-foreground", soft: "bg-joy-sky/15" },
  { chip: "bg-joy-mint text-joy-mint-foreground", soft: "bg-joy-mint/15" },
  { chip: "bg-joy-sun text-joy-sun-foreground", soft: "bg-joy-sun/18" },
  { chip: "bg-joy-flame text-joy-flame-foreground", soft: "bg-joy-flame/12" },
  { chip: "bg-joy-berry text-joy-berry-foreground", soft: "bg-joy-berry/12" },
  { chip: "bg-joy-aqua text-joy-aqua-foreground", soft: "bg-joy-aqua/15" },
];
const toneFor = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    // `?? 0` no es defensivo por gusto: `codePointAt` devuelve `undefined` si el
    // índice cae fuera, la suma da NaN y `NaN >>> 0` es 0 — TODOS los cursos
    // saldrían del mismo color sin que nada fallara. Un bug de UI silencioso.
    h = (h * 31 + (id.codePointAt(i) ?? 0)) >>> 0;
  }
  return JOY_TONES[h % JOY_TONES.length]!;
};

export const CatalogView: Component = () => {
  const { t } = useI18n();
  const [q, setQ] = createSignal("");
  // B5 · N5: el carril «Gratis» es un GANCHO, no un filtro más — por eso vive
  // arriba, junto al buscador, y no escondido en un desplegable de facetas.
  const [onlyFree, setOnlyFree] = createSignal(false);
  const catalog = createQuery<CatalogEntryDTO[]>(() => ({
    queryKey: courseQueryKeys.catalogList(q()),
    queryFn: ({ signal }) => fetchCatalog(SERVER_URL, { q: q(), signal }),
    staleTime: 30_000,
  }));

  // `isFree` lo DERIVA el servidor de la ausencia de plan de precio: aquí solo
  // se filtra, nunca se recalcula (el precio tiene una sola fuente).
  const freeCount = createMemo(() => (catalog.data ?? []).filter((c) => c.isFree).length);
  const shown = createMemo(() =>
    onlyFree() ? (catalog.data ?? []).filter((c) => c.isFree) : (catalog.data ?? []),
  );

  // El ámbito NO incluye la búsqueda a propósito: escribir en el buscador
  // refresca la parrilla varias veces por segundo y re-animarla en cada
  // pulsación sería justo el parpadeo que queremos evitar.
  const isEntering = useListEntrance({
    scope: () => (onlyFree() ? "catalog:free" : "catalog"),
    hasItems: () => shown().length > 0,
  });

  return (
    <div class="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <div class="space-mesh flex flex-col gap-3 rounded-3xl border border-border p-5 md:flex-row md:items-center md:justify-between md:p-6">
        <div>
          <h1 class="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
            {t(msg`Catalog`)}
          </h1>
          <p class="text-sm text-muted-foreground">
            {t(msg`Browse and enroll — no account needed.`)}
          </p>
        </div>
        <div class="relative">
          <Search class="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            data-testid="catalog-search"
            value={q()}
            onInput={(e) => setQ(e.currentTarget.value)}
            placeholder={t(msg`Search courses`)}
            class="w-full rounded-2xl border-2 border-border bg-surface py-2.5 pl-10 pr-3 font-medium transition-colors focus-visible:border-accent focus-visible:outline-none md:w-72"
          />
        </div>
      </div>

      {/* Carril «Gratis»: solo aparece si hay algo gratis que enseñar — un
          conmutador que siempre filtra a cero es ruido, no un gancho. */}
      <Show when={freeCount() > 0}>
        <div class="flex flex-wrap items-center gap-2" data-testid="catalog-free-lane">
          <button
            type="button"
            data-testid="catalog-free-toggle"
            aria-pressed={onlyFree()}
            onClick={() => setOnlyFree((v) => !v)}
            class={cn(
              "flex items-center gap-1.5 rounded-full border-2 px-3.5 py-1.5 text-sm font-semibold",
              "transition-[background-color,border-color,color] duration-200",
              onlyFree()
                ? "border-transparent bg-joy-mint text-joy-mint-foreground"
                : "border-border bg-surface hover:border-accent/40",
            )}
          >
            <Gift class="h-4 w-4" />
            {t(msg`Free`)}
            <span class="opacity-70">({freeCount()})</span>
          </button>
          <p class="text-sm text-muted-foreground">
            {t(msg`Start with a free workshop — no card, no commitment.`)}
          </p>
        </div>
      </Show>

      <Show
        when={!catalog.isLoading}
        fallback={
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <For each={Array.from({ length: 6 })}>
              {() => <Skeleton class="h-44 w-full rounded-3xl" />}
            </For>
          </div>
        }
      >
        <Show
          when={shown().length > 0}
          fallback={
            <div
              class="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border p-14 text-center"
              data-testid="catalog-empty"
            >
              <Mascot mood="think" class="mb-4 h-24 w-24" />
              <h3 class="font-display text-lg font-bold">{t(msg`No courses published yet`)}</h3>
              <p class="mt-1 text-sm text-muted-foreground">
                {t(msg`Create the first one from Authoring.`)}
              </p>
            </div>
          }
        >
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
            <For each={shown()}>
              {(course, index) => (
                <CatalogCard course={course} entering={isEntering()} index={index()} />
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};

const CatalogCard: Component<{ course: CatalogEntryDTO; entering: boolean; index: number }> = (
  props,
) => {
  const { t } = useI18n();
  const tone = () => toneFor(props.course.id);
  const entrance = () => listEntranceProps(props.entering, props.index);
  return (
    <Link
      to="/catalog/$courseId"
      params={{ courseId: props.course.id }}
      // `transition-all` animaba TODAS las propiedades (incluidas las que
      // fuerzan layout). Lista explícita: transform es de compositor y la
      // sombra vive en una tarjeta pequeña, no en una superficie a pantalla
      // completa.
      class={cn(
        "group relative block overflow-hidden rounded-3xl border border-border bg-surface p-5 shadow-sm",
        "transition-[transform,border-color,box-shadow] duration-200 ease-out",
        "hover:-translate-y-1 hover:border-accent/40 hover:shadow-lg active:scale-[0.99]",
        entrance().class,
      )}
      style={entrance().style}
      data-testid="catalog-card"
    >
      <div
        class={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full ${tone().soft}`}
      />
      <div class="relative mb-3 flex items-center gap-2">
        <div
          class={`flex h-11 w-11 items-center justify-center rounded-2xl shadow-sm transition-transform group-hover:scale-105 ${tone().chip}`}
        >
          <BookOpen class="h-5 w-5" />
        </div>
        <Show when={props.course.category}>
          <span class={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone().soft}`}>
            {props.course.category}
          </span>
        </Show>
        {/* B5 · las tres señales del gancho, por orden de fuerza comercial:
            gratis > taller > tiene clase muestra. Nunca las tres a la vez: un
            curso gratis no necesita anunciar que tiene muestra. */}
        <Show when={props.course.isFree}>
          <span
            data-testid="catalog-free-badge"
            class="flex items-center gap-1 rounded-full bg-joy-mint px-2.5 py-0.5 text-xs font-bold text-joy-mint-foreground"
          >
            <Gift class="h-3 w-3" />
            {isWorkshop(props.course.programKind) ? t(msg`Free workshop`) : t(msg`Free`)}
          </span>
        </Show>
        <Show when={!props.course.isFree && props.course.freePreviewLessons > 0}>
          <span
            data-testid="catalog-preview-badge"
            class="flex items-center gap-1 rounded-full bg-joy-sun px-2.5 py-0.5 text-xs font-bold text-joy-sun-foreground"
          >
            <Sparkles class="h-3 w-3" />
            {t(msg`{count} free sample classes`, { count: props.course.freePreviewLessons })}
          </span>
        </Show>
      </div>
      <h3 class="relative line-clamp-2 font-display font-bold group-hover:text-accent">
        {props.course.title}
      </h3>
      <Show when={props.course.description}>
        <p class="relative mt-1 line-clamp-2 text-sm text-muted-foreground">
          {props.course.description}
        </p>
      </Show>
      <div class="relative mt-4 flex items-center gap-4 text-xs font-medium text-muted-foreground">
        <span>{t(msg`{count} lessons`, { count: props.course.lessonCount })}</span>
        <span class="flex items-center gap-1">
          <Users class="h-3 w-3" /> {props.course.enrolledCount}
        </span>
        <Show when={props.course.authorName}>
          {(name) => <span class="ml-auto truncate">{t(msg`by {name}`, { name: name() })}</span>}
        </Show>
      </div>
    </Link>
  );
};
